package com.macrofocus.eflux.ui.morphing

import org.locationtech.jts.geom.Coordinate
import org.locationtech.jts.geom.GeometryFactory
import org.locationtech.jts.geom.LineString
import org.locationtech.jts.geom.Polygon
import org.locationtech.jts.linearref.LengthIndexedLine

fun normalizeDistances(line: LineString): List<Double> {
    val totalLength = line.length
    val distances = mutableListOf(0.0)

    for (i in 1 until line.numPoints) {
        val segmentLength = line.getPointN(i - 1).distance(line.getPointN(i))
        distances.add(distances.last() + segmentLength)
    }

    return distances.map { it / totalLength } // Normalize to [0, 1]
}

fun interpolatePoint(line: LineString, t: Double): Coordinate {
    val indexedLine = LengthIndexedLine(line)
    val lengthAtT = t * line.length
    return indexedLine.extractPoint(lengthAtT)
}

fun interpolatePoint(line: Polygon, t: Double): Coordinate {
    val indexedLine = LengthIndexedLine(line.exteriorRing!!)
    val lengthAtT = t * line.length
    return indexedLine.extractPoint(lengthAtT)
}

fun morphLinesParametric(
    line1: LineString,
    line2: LineString,
    step: Double,
    steps: Int,
    tolerance: Double? = null // Optional tolerance for simplifying the output
): LineString {
    if(step >= steps) {
        return line2
    }

    if(step <= 0) {
        return line1
    }

    val geometryFactory = GeometryFactory()

    val interpolationFactor = step / steps
    val intermediateCoords = mutableListOf<Coordinate>()

    for (t in 0..100) {
        val parameterT = t.toDouble() / 100
        val coord1 = interpolatePoint(line1, parameterT)
        val coord2 = interpolatePoint(line2, parameterT)

        // Morph between the two points
        val element = morphCoordinates(coord1, coord2, step, steps)
        intermediateCoords.add(element)
    }

    // Create a LineString for the current step
    val morphedLine = geometryFactory.createLineString(intermediateCoords.toTypedArray())

    // Simplify if tolerance is provided
    val simplifiedLine = if (tolerance != null) simplifyLine(morphedLine, tolerance) else morphedLine

    return simplifiedLine
}

fun morphLinesParametric(
    line1: Polygon,
    line2: Polygon,
    step: Double,
    steps: Int,
    tolerance: Double? = null // Optional tolerance for simplifying the output
): Polygon {
    if(step >= steps) {
        return line2
    }

    if(step <= 0) {
        return line1
    }

    val geometryFactory = GeometryFactory()

    val interpolationFactor = step / steps
    val intermediateCoords = mutableListOf<Coordinate>()

    for (t in 0..32) {
        val parameterT = t.toDouble() / 32
        val coord1 = interpolatePoint(line1, parameterT)
        val coord2 = interpolatePoint(line2, parameterT)

        // Morph between the two points
        val element = morphCoordinates(coord1, coord2, step, steps)
        intermediateCoords.add(element)
    }

    // Create a LineString for the current step
    val morphedLine = geometryFactory.createPolygon(intermediateCoords.toTypedArray())

    // Simplify if tolerance is provided
    val simplifiedLine = if (tolerance != null) simplifyPolygon(morphedLine, tolerance) else morphedLine

    return simplifiedLine
}

fun morphCoordinates(
    coord1: Coordinate,
    coord2: Coordinate,
    step: Double,
    steps: Int,
): Coordinate {
    if(step >= steps) {
        return coord2
    }

    if(step <= 0) {
        return coord1
    }

    val interpolationFactor = step / steps
    val morphedX = (1 - interpolationFactor) * coord1.x + interpolationFactor * coord2.x
    val morphedY = (1 - interpolationFactor) * coord1.y + interpolationFactor * coord2.y
    val element = Coordinate(morphedX, morphedY)
    return element
}

